import React, {useState, useEffect, useCallback, useRef, useMemo} from 'react';
import {
  View,
  StyleSheet,
  Image,
  TouchableWithoutFeedback,
  ActivityIndicator,
  useWindowDimensions,
  StatusBar,
  Platform,
  PanResponder,
  GestureResponderEvent,
  PanResponderGestureState,
  AppState,
  AppStateStatus,
  BackHandler,
} from 'react-native';
import {useRouting} from '../hooks/useRouting';
import {useTranslation} from 'react-i18next';
import {colors} from '../constants';
import {useAppSelector} from '../store';
import {frameService, photoService} from '../services';
import {cacheFrameData, loadCachedFrameData} from '../services/offlineCache';
import {storage} from '../services/storage';
import {FrameData, Photo} from '../types';
import {useSettingsNavigation} from '../hooks/useSettingsNavigation';
import {getTemplate, DEFAULT_TEMPLATE_ID, TemplateProps} from '../templates';
import {useScreenWakeLock} from '../hooks/useScreenWakeLock';

const PHOTO_INDEX_KEY = 'oma_frame_current_photo_index';
const LAST_VIEWED_DISPLAY_KEY = 'oma_last_viewed_display_id';

// Placeholder demo photos
const DEMO_PHOTOS = [
  'https://picsum.photos/1200/800?random=1',
  'https://picsum.photos/1200/800?random=2',
  'https://picsum.photos/1200/800?random=3',
];

// Fetch an image from a URL and convert to data URL for caching
async function fetchImageAsDataUrl(url: string): Promise<string> {
  const response = await fetch(url);
  if (!response.ok) {
    throw new Error(`Failed to fetch image: ${response.status}`);
  }
  const blob = await response.blob();
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(reader.result as string);
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
}

function getGreetingKey(): string {
  const hour = new Date().getHours();

  if (hour >= 5 && hour < 12) {
    return 'greetings.morning';
  } else if (hour >= 12 && hour < 17) {
    return 'greetings.afternoon';
  } else if (hour >= 17 && hour < 21) {
    return 'greetings.evening';
  } else {
    return 'greetings.night';
  }
}

function formatDate(): {short: string; weekday: string} {
  const now = new Date();
  const weekday = now.toLocaleDateString(undefined, {weekday: 'short'});
  const monthDay = now.toLocaleDateString(undefined, {month: 'short', day: 'numeric'});
  return {short: `${weekday}, ${monthDay}`, weekday};
}

function formatTime(): string {
  const now = new Date();
  return now.toLocaleTimeString(undefined, {
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  });
}

export default function FrameScreen() {
  useScreenWakeLock();
  const {t} = useTranslation();
  const {navigate} = useSettingsNavigation('Frame');
  const {user} = useAppSelector(state => state.auth);
  const displayName = user?.displayName || 'Oma';
  const windowDimensions = useWindowDimensions();
  const {locationKey, getSearchParam} = useRouting();
  const displayId = getSearchParam('displayId') || undefined;

  // Track actual container dimensions (may differ from window in split view)
  const [containerSize, setContainerSize] = useState<{width: number; height: number} | null>(null);
  const handleContainerLayout = useCallback((event: {nativeEvent: {layout: {width: number; height: number}}}) => {
    const {width: layoutWidth, height: layoutHeight} = event.nativeEvent.layout;
    setContainerSize(prev => {
      // Only update if dimensions actually changed to avoid unnecessary re-renders
      if (prev && prev.width === layoutWidth && prev.height === layoutHeight) {
        return prev;
      }
      return {width: layoutWidth, height: layoutHeight};
    });
  }, []);

  // Use container dimensions if available, otherwise fall back to window dimensions
  const width = containerSize?.width ?? windowDimensions.width;
  const height = containerSize?.height ?? windowDimensions.height;
  const isPortrait = height > width || width < 768;

  const [frameData, setFrameData] = useState<FrameData | null>(null);
  const [photoDataUrls, setPhotoDataUrls] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [loadingProgress, setLoadingProgress] = useState<{loaded: number; total: number} | null>(null);
  const [currentPhotoIndex, setCurrentPhotoIndex] = useState(0);
  const [previousPhotoIndex, setPreviousPhotoIndex] = useState<number | null>(null);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [showHint, setShowHint] = useState(false);
  const [currentTime, setCurrentTime] = useState(formatTime());
  const [currentDate, setCurrentDate] = useState(formatDate());
  const [greetingKey, setGreetingKey] = useState(getGreetingKey());

  // Compose the greeting with translation
  const greeting = `${t(greetingKey)}, ${displayName}`;

  // Edit mode state for photo repositioning
  const [isEditMode, setIsEditMode] = useState(false);
  const [panOffset, setPanOffset] = useState({x: 0, y: 0});
  const [isSaving, setIsSaving] = useState(false);
  const [showSaved, setShowSaved] = useState(false);
  const [imageDimensions, setImageDimensions] = useState({width: 0, height: 0});
  const longPressTimerRef = useRef<NodeJS.Timeout | null>(null);
  const saveTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const editModeTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const transitionTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const currentPhotoIndexRef = useRef(currentPhotoIndex);
  currentPhotoIndexRef.current = currentPhotoIndex;

  // Template selection - reads from frame data settings
  const templateId = frameData?.settings?.frameTemplateId || DEFAULT_TEMPLATE_ID;
  const template = useMemo(() => getTemplate(templateId), [templateId]);
  const TemplateComponent = template.component;

  // Restore last viewed photo index on mount
  useEffect(() => {
    const restorePhotoIndex = async () => {
      try {
        const savedIndex = await storage.getItem(PHOTO_INDEX_KEY);
        if (savedIndex !== null) {
          const index = parseInt(savedIndex, 10);
          if (!isNaN(index) && index >= 0) {
            setCurrentPhotoIndex(index);
          }
        }
      } catch (error) {
        console.warn('[Frame] Failed to restore photo index:', error);
      }
    };
    restorePhotoIndex();
  }, []);

  // Save photo index when it changes
  useEffect(() => {
    if (photoDataUrls.length > 0) {
      storage.setItem(PHOTO_INDEX_KEY, currentPhotoIndex.toString()).catch(() => {});
    }
  }, [currentPhotoIndex, photoDataUrls.length]);

  // Transition to next photo with crossfade
  const transitionToPhoto = useCallback((newIndex: number) => {
    if (isTransitioning) return;

    setPreviousPhotoIndex(currentPhotoIndex);
    setIsTransitioning(true);
    setCurrentPhotoIndex(newIndex);

    // End transition after animation completes
    if (transitionTimeoutRef.current) {
      clearTimeout(transitionTimeoutRef.current);
    }
    transitionTimeoutRef.current = setTimeout(() => {
      setIsTransitioning(false);
      setPreviousPhotoIndex(null);
    }, 800); // Match CSS transition duration
  }, [currentPhotoIndex, isTransitioning]);

  const loadFrameData = useCallback(async (skipCache = false) => {
    // First, try to load from cache for instant display (offline support)
    if (!skipCache) {
      try {
        const cached = await loadCachedFrameData();
        if (cached) {
          setFrameData(cached.frameData);
          setPhotoDataUrls(cached.photoDataUrls);
          setGreetingKey(getGreetingKey());
          setIsLoading(false);
          console.log('[Frame] Loaded from cache, will refresh in background');
        }
      } catch (error) {
        console.warn('[Frame] Cache load failed:', error);
      }
    }

    // Then fetch fresh data from the API
    try {
      const data = await frameService.getFrameData(displayId);
      setFrameData(data);
      setGreetingKey(getGreetingKey());

      // Save the displayId for "back to photos" navigation
      if (data.displayId) {
        storage.setItem(LAST_VIEWED_DISPLAY_KEY, data.displayId);
      }

      if (!data.photos || data.photos.length === 0) {
        // No photos assigned to this display - clear any cached photos
        setPhotoDataUrls([]);
        setIsLoading(false);
      } else {
        // Load photos progressively - show each as it becomes ready
        const totalPhotos = data.photos.length;

        // Check if we already have photos displayed (background refresh case)
        // We'll use a function to get the current state to avoid stale closure
        let existingUrls: string[] = [];
        setPhotoDataUrls(prev => {
          existingUrls = prev;
          return prev; // Don't change state yet
        });

        const hasExistingPhotos = existingUrls.length > 0 && existingUrls.some(url => url && !url.includes('picsum.photos'));

        // Only show loading progress on initial load (not background refresh)
        if (!hasExistingPhotos && totalPhotos > 1) {
          setLoadingProgress({loaded: 0, total: totalPhotos});
        }

        // Start with existing URLs (if background refresh) or empty placeholders (if initial load)
        const urlsInProgress: string[] = hasExistingPhotos
          ? [...existingUrls] // Preserve existing photos during refresh
          : new Array(totalPhotos).fill('');

        // If photo count changed, adjust the array size
        if (urlsInProgress.length !== totalPhotos) {
          if (urlsInProgress.length < totalPhotos) {
            // More photos now - add empty slots
            while (urlsInProgress.length < totalPhotos) {
              urlsInProgress.push('');
            }
          } else {
            // Fewer photos now - truncate
            urlsInProgress.length = totalPhotos;
          }
        }

        let loadedCount = 0;

        // Helper to load a single photo and update state
        const loadPhoto = async (index: number) => {
          try {
            const presignedUrl = await photoService.getPhotoUrl(data.photos[index].id);
            const dataUrl = await fetchImageAsDataUrl(presignedUrl);
            urlsInProgress[index] = dataUrl;
            loadedCount++;
            // Update state with current progress
            setPhotoDataUrls([...urlsInProgress]);
            if (!hasExistingPhotos) {
              setLoadingProgress({loaded: loadedCount, total: totalPhotos});
            }
          } catch (error) {
            console.error(`Failed to load photo ${data.photos[index].id}`, error);
            // Only use placeholder if we don't have an existing photo at this index
            if (!urlsInProgress[index] || urlsInProgress[index].includes('picsum.photos')) {
              urlsInProgress[index] = DEMO_PHOTOS[0];
            }
            loadedCount++;
            setPhotoDataUrls([...urlsInProgress]);
            if (!hasExistingPhotos) {
              setLoadingProgress({loaded: loadedCount, total: totalPhotos});
            }
          }
        };

        // Load current photo first for immediate display
        const currentIdx = currentPhotoIndexRef.current % data.photos.length;
        await loadPhoto(currentIdx);
        setIsLoading(false);

        // Load remaining photos in background (in order from current)
        const remainingIndices = [];
        for (let i = 1; i < data.photos.length; i++) {
          remainingIndices.push((currentIdx + i) % data.photos.length);
        }

        // Load remaining photos in parallel (but don't wait for them)
        Promise.all(remainingIndices.map(loadPhoto)).then(() => {
          // Cache the complete data for offline use
          cacheFrameData(data, urlsInProgress);
          // Hide progress bar after a short delay
          if (!hasExistingPhotos) {
            setTimeout(() => setLoadingProgress(null), 1000);
          }
        });
      }
    } catch (error) {
      // API failed (likely offline) - keep using cached data if available
      console.log('[Frame] API fetch failed, using cached data:', error);
    } finally {
      setIsLoading(false);
    }
  }, [displayId]);

  // Load frame data on mount and when location/displayId changes
  useEffect(() => {
    loadFrameData();
  }, [locationKey, displayId, loadFrameData]);

  // Poll for updated data every 5 minutes (skip cache on poll, just try API)
  useEffect(() => {
    const pollInterval = setInterval(() => {
      loadFrameData(true); // skipCache = true for polling
    }, 5 * 60 * 1000);

    return () => clearInterval(pollInterval);
  }, [loadFrameData]);

  // Refresh at midnight to ensure reminders are up-to-date after date change
  useEffect(() => {
    let currentDate = new Date().toDateString();

    const midnightCheck = setInterval(() => {
      const newDate = new Date().toDateString();
      if (newDate !== currentDate) {
        console.log('[Frame] Date changed, refreshing data');
        currentDate = newDate;
        loadFrameData(true); // skipCache = true for fresh data
      }
    }, 60 * 1000); // Check every minute

    return () => clearInterval(midnightCheck);
  }, [loadFrameData]);

  // Hide status bar on mobile for full-screen frame experience
  useEffect(() => {
    if (Platform.OS !== 'web') {
      StatusBar.setHidden(true, 'fade');
      // Also set translucent for Android to allow content behind status bar area
      if (Platform.OS === 'android') {
        StatusBar.setTranslucent(true);
      }
    }
    return () => {
      // Restore status bar when leaving frame screen
      if (Platform.OS !== 'web') {
        StatusBar.setHidden(false, 'fade');
      }
    };
  }, []);

  // Show hint for first-time users (per user)
  useEffect(() => {
    if (!user?.id || isLoading) return;

    // localStorage only available on web
    if (Platform.OS === 'web') {
      const hintKey = `oma_frame_hint_seen_${user.id}`;
      const hasSeenHint = localStorage.getItem(hintKey);

      if (!hasSeenHint) {
        // Small delay so frame loads first
        const showTimer = setTimeout(() => {
          setShowHint(true);
        }, 1500);

        return () => clearTimeout(showTimer);
      }
    }
  }, [isLoading, user?.id]);

  const dismissHint = useCallback(() => {
    if (user?.id) {
      setShowHint(false);
      // localStorage only available on web
      if (Platform.OS === 'web') {
        localStorage.setItem(`oma_frame_hint_seen_${user.id}`, 'true');
      }
    }
  }, [user?.id]);

  // Get current photo object for offset values
  const currentPhoto: Photo | null = useMemo(() => {
    if (!frameData?.photos || frameData.photos.length === 0) return null;
    return frameData.photos[currentPhotoIndex % frameData.photos.length];
  }, [frameData?.photos, currentPhotoIndex]);

  // Get previous photo object
  const previousPhoto: Photo | null = useMemo(() => {
    if (!frameData?.photos || frameData.photos.length === 0 || previousPhotoIndex === null) return null;
    return frameData.photos[previousPhotoIndex % frameData.photos.length];
  }, [frameData?.photos, previousPhotoIndex]);

  // Calculate photo section dimensions based on template ratio
  const photoSectionSize = useMemo(() => {
    const ratio = template.photoSectionRatio;
    if (!ratio) {
      // Default: full screen for photo
      return {width, height};
    }
    const ratioForOrientation = isPortrait ? ratio.portrait : ratio.landscape;
    return {
      width: width * ratioForOrientation.width,
      height: height * ratioForOrientation.height,
    };
  }, [width, height, isPortrait, template.photoSectionRatio]);

  // Calculate scaled image dimensions and max pan values
  // When image aspect ratio differs from photo section, there's room to pan
  const {scaledImageSize, maxPan} = useMemo(() => {
    const photoWidth = photoSectionSize.width;
    const photoHeight = photoSectionSize.height;
    const defaultSize = {width: photoWidth * 1.5, height: photoHeight * 1.5};
    const defaultPan = {x: photoWidth * 0.25, y: photoHeight * 0.25};

    if (imageDimensions.width === 0 || imageDimensions.height === 0) {
      return {scaledImageSize: defaultSize, maxPan: defaultPan};
    }
    const imageAspect = imageDimensions.width / imageDimensions.height;
    const sectionAspect = photoWidth / photoHeight;

    // Calculate scale to cover photo section (like object-fit: cover)
    let scaledWidth: number;
    let scaledHeight: number;

    if (imageAspect > sectionAspect) {
      // Image is wider - scale by height, extend horizontally
      scaledHeight = photoHeight;
      scaledWidth = photoHeight * imageAspect;
    } else {
      // Image is taller - scale by width, extend vertically
      scaledWidth = photoWidth;
      scaledHeight = photoWidth / imageAspect;
    }

    // Add extra padding (20%) so there's always some room to pan
    const extraPadding = 1;
    scaledWidth *= extraPadding;
    scaledHeight *= extraPadding;

    // Max pan is half the overflow in each direction
    const panX = Math.max(0, (scaledWidth - photoWidth) / 2);
    const panY = Math.max(0, (scaledHeight - photoHeight) / 2);

    return {
      scaledImageSize: {width: scaledWidth, height: scaledHeight},
      maxPan: {x: panX, y: panY},
    };
  }, [imageDimensions, photoSectionSize, containerSize, width, height]);

  // Load image dimensions when photo changes
  useEffect(() => {
    const url = photoDataUrls[currentPhotoIndex];
    if (!url) return;

    // Reset pan offset to saved value when photo changes (but not during edit mode)
    if (currentPhoto && !isEditMode) {
      setPanOffset({
        x: currentPhoto.offsetX * maxPan.x,
        y: currentPhoto.offsetY * maxPan.y,
      });
    }

    // Get image dimensions using RN Image.getSize with fallback
    Image.getSize(
      url,
      (imgWidth, imgHeight) => {
        setImageDimensions({width: imgWidth, height: imgHeight});
      },
      () => {
        // Fallback: assume image is larger than screen for panning
        setImageDimensions({width: width * 1.5, height: height * 1.5});
      }
    );
  }, [currentPhotoIndex, photoDataUrls, currentPhoto?.id, isEditMode, width, height]);

  // Update pan offset when maxPan changes (e.g., on orientation change) - but not during edit
  useEffect(() => {
    if (isEditMode) return; // Don't reset while editing
    if (currentPhoto && (maxPan.x > 0 || maxPan.y > 0)) {
      setPanOffset({
        x: currentPhoto.offsetX * maxPan.x,
        y: currentPhoto.offsetY * maxPan.y,
      });
    }
  }, [maxPan.x, maxPan.y, currentPhoto?.offsetX, currentPhoto?.offsetY, isEditMode]);

  // Ref to track current pan offset for use in callbacks (avoids stale closure)
  const panOffsetRef = useRef(panOffset);
  panOffsetRef.current = panOffset;

  // Save photo position and update local state
  const savePhotoPosition = useCallback(async (offsetX: number, offsetY: number) => {
    if (!currentPhoto?.id || !frameData) return;

    // Normalize offsets to -1 to 1 range
    const normalizedX = maxPan.x > 0 ? offsetX / maxPan.x : 0;
    const normalizedY = maxPan.y > 0 ? offsetY / maxPan.y : 0;

    setIsSaving(true);
    try {
      await photoService.updatePhotoPosition(currentPhoto.id, normalizedX, normalizedY);

      // Update local state so the photo keeps its position after exiting edit mode
      setFrameData(prev => {
        if (!prev) return prev;
        return {
          ...prev,
          photos: prev.photos.map(photo =>
            photo.id === currentPhoto.id
              ? {...photo, offsetX: normalizedX, offsetY: normalizedY}
              : photo
          ),
        };
      });

      setShowSaved(true);
      setTimeout(() => setShowSaved(false), 1500);
    } catch (error) {
      console.error('Failed to save photo position', error);
    } finally {
      setIsSaving(false);
    }
  }, [currentPhoto?.id, maxPan.x, maxPan.y, frameData]);

  // Enter edit mode
  const enterEditMode = useCallback(() => {
    setIsEditMode(true);
    setShowSettings(false);

    // Auto-exit after 30 seconds of inactivity
    if (editModeTimeoutRef.current) {
      clearTimeout(editModeTimeoutRef.current);
    }
    editModeTimeoutRef.current = setTimeout(() => {
      setIsEditMode(false);
    }, 30000);
  }, []);

  // Exit edit mode - save position first, then exit
  const exitEditMode = useCallback(async () => {
    // Clear any pending save timeout
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
      saveTimeoutRef.current = null;
    }

    // Save the current position immediately before exiting
    await savePhotoPosition(panOffsetRef.current.x, panOffsetRef.current.y);

    setIsEditMode(false);
    if (editModeTimeoutRef.current) {
      clearTimeout(editModeTimeoutRef.current);
      editModeTimeoutRef.current = null;
    }
  }, [savePhotoPosition]);

  // Reset edit mode timeout on interaction
  const resetEditModeTimeout = useCallback(() => {
    if (editModeTimeoutRef.current) {
      clearTimeout(editModeTimeoutRef.current);
    }
    editModeTimeoutRef.current = setTimeout(() => {
      setIsEditMode(false);
    }, 30000);
  }, []);

  // Track the starting offset and position when drag begins
  const dragStartOffset = useRef({x: 0, y: 0});
  const dragStartPos = useRef({x: 0, y: 0});
  const isDragging = useRef(false);

  // Native PanResponder for drag gestures (Android/iOS)
  const panResponder = useMemo(() => {
    if (Platform.OS === 'web') return null;

    return PanResponder.create({
      onStartShouldSetPanResponder: () => isEditMode,
      onMoveShouldSetPanResponder: () => isEditMode,
      onPanResponderGrant: () => {
        dragStartOffset.current = {x: panOffsetRef.current.x, y: panOffsetRef.current.y};
        isDragging.current = true;
        resetEditModeTimeout();
      },
      onPanResponderMove: (_: GestureResponderEvent, gestureState: PanResponderGestureState) => {
        if (!isEditMode) return;

        const newX = Math.max(-maxPan.x, Math.min(maxPan.x, dragStartOffset.current.x + gestureState.dx));
        const newY = Math.max(-maxPan.y, Math.min(maxPan.y, dragStartOffset.current.y + gestureState.dy));

        setPanOffset({x: newX, y: newY});
      },
      onPanResponderRelease: () => {
        isDragging.current = false;
        // Save position after drag ends (debounced)
        if (saveTimeoutRef.current) {
          clearTimeout(saveTimeoutRef.current);
        }
        saveTimeoutRef.current = setTimeout(() => {
          savePhotoPosition(panOffsetRef.current.x, panOffsetRef.current.y);
        }, 500);
      },
      onPanResponderTerminate: () => {
        isDragging.current = false;
      },
    });
  }, [isEditMode, maxPan.x, maxPan.y, resetEditModeTimeout, savePhotoPosition]);

  // Web-native drag handlers (more reliable than PanResponder on web)
  const handleDragStart = useCallback((e: React.MouseEvent | React.TouchEvent) => {
    if (!isEditMode) return;
    isDragging.current = true;

    const clientX = 'touches' in e ? e.touches[0].clientX : e.clientX;
    const clientY = 'touches' in e ? e.touches[0].clientY : e.clientY;

    dragStartPos.current = {x: clientX, y: clientY};
    dragStartOffset.current = {x: panOffsetRef.current.x, y: panOffsetRef.current.y};
    resetEditModeTimeout();
  }, [isEditMode, resetEditModeTimeout]);

  const handleDragMove = useCallback((e: React.MouseEvent | React.TouchEvent) => {
    if (!isDragging.current || !isEditMode) return;

    const clientX = 'touches' in e ? e.touches[0].clientX : e.clientX;
    const clientY = 'touches' in e ? e.touches[0].clientY : e.clientY;

    const dx = clientX - dragStartPos.current.x;
    const dy = clientY - dragStartPos.current.y;

    const newX = Math.max(-maxPan.x, Math.min(maxPan.x, dragStartOffset.current.x + dx));
    const newY = Math.max(-maxPan.y, Math.min(maxPan.y, dragStartOffset.current.y + dy));

    setPanOffset({x: newX, y: newY});
  }, [isEditMode, maxPan.x, maxPan.y]);

  const handleDragEnd = useCallback(() => {
    if (!isDragging.current) return;
    isDragging.current = false;

    // Save position after drag ends (debounced)
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }
    saveTimeoutRef.current = setTimeout(() => {
      savePhotoPosition(panOffsetRef.current.x, panOffsetRef.current.y);
    }, 500);
  }, [savePhotoPosition]);

  // Long press handler for entering edit mode
  const handleLongPressStart = useCallback(() => {
    if (showHint || isEditMode) return;
    longPressTimerRef.current = setTimeout(() => {
      enterEditMode();
    }, 800);
  }, [showHint, isEditMode, enterEditMode]);

  const handleLongPressEnd = useCallback(() => {
    if (longPressTimerRef.current) {
      clearTimeout(longPressTimerRef.current);
      longPressTimerRef.current = null;
    }
  }, []);

  // Cleanup timeouts
  useEffect(() => {
    return () => {
      if (longPressTimerRef.current) clearTimeout(longPressTimerRef.current);
      if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current);
      if (editModeTimeoutRef.current) clearTimeout(editModeTimeoutRef.current);
      if (transitionTimeoutRef.current) clearTimeout(transitionTimeoutRef.current);
    };
  }, []);

  // Update time every minute
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(formatTime());
      setCurrentDate(formatDate());
      setGreetingKey(getGreetingKey());
    }, 60000);

    return () => clearInterval(timer);
  }, [displayName]);

  // Photo cycling (paused during edit mode and transitions)
  useEffect(() => {
    const cycleEnabled = frameData?.settings?.photoCycleEnabled ?? true;
    const intervalMs = frameData?.settings?.photoCycleIntervalMs ?? 30000;

    // Don't cycle while in edit mode or during transition
    if (!cycleEnabled || photoDataUrls.length <= 1 || isEditMode || isTransitioning) return;

    const timer = setInterval(() => {
      const nextIndex = (currentPhotoIndex + 1) % photoDataUrls.length;
      transitionToPhoto(nextIndex);
    }, intervalMs);

    return () => clearInterval(timer);
  }, [frameData, photoDataUrls, isEditMode, isTransitioning, currentPhotoIndex, transitionToPhoto]);

  // Hide settings button after 3 seconds
  useEffect(() => {
    if (showSettings) {
      const timer = setTimeout(() => {
        setShowSettings(false);
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [showSettings]);

  // Refresh time/data when app returns from background (native only)
  useEffect(() => {
    if (Platform.OS === 'web') return;

    const appStateRef = {current: AppState.currentState};
    const handleAppStateChange = (nextAppState: AppStateStatus) => {
      if (
        appStateRef.current.match(/inactive|background/) &&
        nextAppState === 'active'
      ) {
        setCurrentTime(formatTime());
        setCurrentDate(formatDate());
        setGreetingKey(getGreetingKey());
        loadFrameData(true);
      }
      appStateRef.current = nextAppState;
    };

    const subscription = AppState.addEventListener('change', handleAppStateChange);
    return () => subscription.remove();
  }, [loadFrameData]);

  // Android back button exits edit mode instead of leaving the app
  useEffect(() => {
    if (Platform.OS === 'web' || !isEditMode) return;

    const handler = BackHandler.addEventListener('hardwareBackPress', () => {
      exitEditMode();
      return true;
    });

    return () => handler.remove();
  }, [isEditMode, exitEditMode]);

  const handleTap = useCallback(() => {
    // Don't toggle settings if hint is showing - user should dismiss hint first
    if (showHint) return;
    setShowSettings(prev => !prev);
  }, [showHint]);

  const handleSettingsPress = useCallback(() => {
    navigate('Settings');
  }, [navigate]);

  // Get photo URL, falling back to demo if not loaded yet (empty string)
  const getPhotoUrl = (index: number) => {
    if (photoDataUrls.length === 0) {
      return DEMO_PHOTOS[index % DEMO_PHOTOS.length];
    }
    const url = photoDataUrls[index % photoDataUrls.length];
    // If URL is empty (not loaded yet), use demo placeholder
    return url || DEMO_PHOTOS[index % DEMO_PHOTOS.length];
  };

  const currentPhotoUrl = getPhotoUrl(currentPhotoIndex);

  const previousPhotoUrl = previousPhotoIndex !== null
    ? getPhotoUrl(previousPhotoIndex)
    : null;

  // Get offset for a specific photo
  const getPhotoOffset = useCallback((photoIndex: number) => {
    if (!frameData?.photos || frameData.photos.length === 0) {
      return {x: 0, y: 0};
    }
    const photo = frameData.photos[photoIndex % frameData.photos.length];
    return {
      x: (photo?.offsetX || 0) * maxPan.x,
      y: (photo?.offsetY || 0) * maxPan.y,
    };
  }, [frameData?.photos, maxPan.x, maxPan.y]);

  const currentOffset = useMemo(() => getPhotoOffset(currentPhotoIndex), [getPhotoOffset, currentPhotoIndex]);
  const previousOffset = useMemo(() => previousPhotoIndex !== null ? getPhotoOffset(previousPhotoIndex) : {x: 0, y: 0}, [getPhotoOffset, previousPhotoIndex]);

  const reminders = frameData?.reminders || [];

  // Loading state
  if (isLoading) {
    return (
      <View style={[styles.container, styles.loadingContainer]} onLayout={handleContainerLayout}>
        <ActivityIndicator size="large" color={colors.frame.primary} />
      </View>
    );
  }

  // Build template props
  const templateProps: TemplateProps = {
    // Photo data
    currentPhoto,
    currentPhotoUrl,
    previousPhoto,
    previousPhotoUrl,
    isTransitioning,
    scaledImageSize,
    currentOffset,
    previousOffset,

    // Screen dimensions
    screenWidth: width,
    screenHeight: height,
    isPortrait,

    // Time & date
    currentTime: new Date(),
    formattedTime: currentTime,
    formattedDate: currentDate,
    greeting,

    // User data
    userName: displayName,
    reminders,

    // UI state
    isEditMode,
    showSettings,
    showHint,
    isSaving,
    showSaved,
    loadingProgress,

    // Edit mode
    panOffset,
    maxPan,

    // Callbacks
    onSettingsPress: handleSettingsPress,
    onEditPress: enterEditMode,
    onExitEditMode: exitEditMode,
    onDismissHint: dismissHint,

    // Edit mode drag handlers
    onDragStart: handleDragStart,
    onDragMove: handleDragMove,
    onDragEnd: handleDragEnd,
    panResponderHandlers: panResponder?.panHandlers || {},

    // Translation
    t,

    // Display settings
    fontScale: frameData?.settings?.fontScale ?? 1.0,
    displayDeviceName: frameData?.displayName,
  };

  // Render template with touch handlers
  return (
    <TouchableWithoutFeedback
      onPress={handleTap}
      onPressIn={handleLongPressStart}
      onPressOut={handleLongPressEnd}>
      <View style={styles.container} onLayout={handleContainerLayout}>
        <TemplateComponent {...templateProps} />
      </View>
    </TouchableWithoutFeedback>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: colors.frame.background,
  },
  loadingContainer: {
    justifyContent: 'center',
    alignItems: 'center',
  },
});
